"use client";

import { type Maybe, type FormSection_Fields } from "~/__generated__/graphql";

import ReCAPTCHA from "react-google-recaptcha";
import { env } from "~/env.mjs";
import { FormProvider, useForm } from "react-hook-form";
import { useRef } from "react";
import { cn } from "~/utils/cn";
import { type PageProps } from "~/slices/FlexibleContent";
import { FormFieldRenderer } from "./FormFieldRenderer";

export function Form({
  formFields,
  submitButtonLabel,
  submitMessage,
  pageData,
}: Pick<
  FormSection_Fields,
  | "title"
  | "formFields"
  | "submitButtonLabel"
  | "submitMessage"
  | "emailTemplateId"
> &
  PageProps) {
  const captchaField = useRef<ReCAPTCHA>(null);

  const methods = useForm({
    mode: "onBlur",
  });

  const {
    register,
    handleSubmit,
    setError,
    control,
    formState: { isSubmitting, isSubmitted, isSubmitSuccessful, errors },
  } = methods;

  const onSubmit = handleSubmit(async (fields) => {
    try {
      const captcha = await captchaField.current?.executeAsync();
      const response = await fetch("api/submitForm", {
        method: "POST",
        body: JSON.stringify({
          data: { uri: pageData.uri, fields },
          captcha,
        }),
      });

      const res = (await response.json()) as {
        message: string;
      };

      if (response.status !== 200) {
        setError("root.serverError", {
          type: response.status.toString(),
          message: res?.message ?? "Something went wrong!",
        });
      }
    } catch (e) {
      setError("root.serverError", {
        type: "500",
        message: "Something went wrong!",
      });
    }
  });

  return (
    <div className="my-6">
      {isSubmitted ? (
        <h3 className="py-8 text-2xl text-white">{submitMessage}</h3>
      ) : (
        <FormProvider {...methods}>
          <form onSubmit={onSubmit}>
            <ReCAPTCHA
              sitekey={env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
              size="invisible"
              ref={captchaField}
            />

            <div className="flex flex-col gap-8">
              <FormFieldRenderer
                fieldGroupKey="FormSectionFormFields"
                slices={
                  formFields as Maybe<
                    Maybe<{ fieldGroupName: string; [key: string]: unknown }>[]
                  >
                }
              />

              <button
                type="submit"
                disabled={isSubmitting || isSubmitSuccessful}
                className={cn(
                  "min-h-[3.5rem] rounded-sm bg-blue px-6 py-1 text-lg font-normal tracking-wide text-white",
                  (isSubmitting || isSubmitSuccessful) && "opacity-50",
                )}
              >
                {isSubmitted &&
                  !isSubmitSuccessful &&
                  errors.root?.serverError &&
                  "Something went wrong! Please try again later."}
                {isSubmitting && !errors.root?.serverError && "Submitting..."}
                {!isSubmitted &&
                  !isSubmitting &&
                  !errors.root?.serverError &&
                  submitButtonLabel}
                {isSubmitted &&
                  isSubmitSuccessful &&
                  !errors.root?.serverError &&
                  "Submitted!"}
              </button>
            </div>
          </form>
        </FormProvider>
      )}
    </div>
  );
}
